import { useId, useRef, useState } from 'react'
import { Reveal } from '@/components/motion/Reveal'
import { SplitText } from '@/components/motion/SplitText'
import { SectionIndex } from '@/components/ui/SectionIndex'
import { useSite } from '@/app/site-context'
import { interpolate } from '@/i18n'
import { contactEndpoint, mailtoHref, siteConfig } from '@/lib/site-config'
import type { ContactErrors, ContactField, ContactValues } from '@/lib/contact-form'
import { buildMailBody, emptyContactValues, validateContact } from '@/lib/contact-form'

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function Contact() {
  const { dictionary, pathFor } = useSite()
  const { contact } = dictionary
  const form = contact.form

  const [values, setValues] = useState<ContactValues>(emptyContactValues)
  const [errors, setErrors] = useState<ContactErrors>({})
  const [status, setStatus] = useState<Status>('idle')
  const honeypot = useRef('')
  const baseId = useId()

  const fieldId = (field: ContactField) => `${baseId}-${field}`
  const errorId = (field: ContactField) => `${baseId}-${field}-error`

  const update = <Field extends ContactField>(field: Field, value: ContactValues[Field]) => {
    setValues((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => {
      if (previous[field] === undefined) return previous
      return Object.fromEntries(Object.entries(previous).filter(([key]) => key !== field))
    })
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (status === 'submitting') return

    const found = validateContact(values, dictionary)
    setErrors(found)

    const firstInvalid = Object.keys(found)[0]
    if (firstInvalid !== undefined) {
      document.getElementById(fieldId(firstInvalid as ContactField))?.focus()
      return
    }

    if (honeypot.current.length > 0) {
      setStatus('success')
      setValues(emptyContactValues)
      return
    }

    if (contactEndpoint.length === 0) {
      window.location.href = mailtoHref(
        `${siteConfig.name} — ${values.name}`,
        buildMailBody(values),
      )
      setStatus('success')
      setValues(emptyContactValues)
      return
    }

    setStatus('submitting')
    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(values),
      })
      if (!response.ok) throw new Error(`Request failed with status ${String(response.status)}`)
      setStatus('success')
      setValues(emptyContactValues)
    } catch {
      setStatus('error')
    }
  }

  const describedBy = (field: ContactField) =>
    errors[field] === undefined ? undefined : errorId(field)

  return (
    <section className="contact section" id="contact" aria-labelledby="contact-title">
      <div className="contact__layout">
        <div className="contact__intro">
          <SectionIndex value="05" />
          <h2 className="contact__title" id="contact-title">
            <SplitText text={contact.title} stagger={40} />
          </h2>
          <Reveal variant="rise" delay={120}>
            <p className="contact__lede">{contact.lede}</p>
            <p className="contact__note">
              {interpolate(contact.responseNote, { days: siteConfig.responseTimeDays })}
            </p>
          </Reveal>

          <Reveal variant="rise" delay={220}>
            <ul className="contact__details">
              <li>
                <span className="contact__detail-label">{contact.emailLabel}</span>
                <a href={`mailto:${siteConfig.salesEmail}`}>{siteConfig.salesEmail}</a>
              </li>
              <li>
                <span className="contact__detail-label">{contact.phoneLabel}</span>
                <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}>{siteConfig.phone}</a>
              </li>
            </ul>
          </Reveal>
        </div>

        <Reveal variant="clip" delay={160} className="contact__form-wrap">
          <form
            className="contact__form"
            onSubmit={(event) => {
              void onSubmit(event)
            }}
            noValidate
          >
            <div className="field">
              <label htmlFor={fieldId('name')}>{form.name}</label>
              <input
                id={fieldId('name')}
                name="name"
                type="text"
                autoComplete="name"
                placeholder={form.namePlaceholder}
                value={values.name}
                aria-invalid={errors.name !== undefined}
                aria-describedby={describedBy('name')}
                onChange={(event) => {
                  update('name', event.target.value)
                }}
              />
              {errors.name === undefined ? null : (
                <p className="field__error" id={errorId('name')}>
                  {errors.name}
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor={fieldId('email')}>{form.email}</label>
              <input
                id={fieldId('email')}
                name="email"
                type="email"
                autoComplete="email"
                placeholder={form.emailPlaceholder}
                value={values.email}
                aria-invalid={errors.email !== undefined}
                aria-describedby={describedBy('email')}
                onChange={(event) => {
                  update('email', event.target.value)
                }}
              />
              {errors.email === undefined ? null : (
                <p className="field__error" id={errorId('email')}>
                  {errors.email}
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor={fieldId('company')}>{form.company}</label>
              <input
                id={fieldId('company')}
                name="company"
                type="text"
                autoComplete="organization"
                placeholder={form.companyPlaceholder}
                value={values.company}
                onChange={(event) => {
                  update('company', event.target.value)
                }}
              />
            </div>

            <div className="field">
              <label htmlFor={fieldId('message')}>{form.message}</label>
              <textarea
                id={fieldId('message')}
                name="message"
                rows={5}
                placeholder={form.messagePlaceholder}
                value={values.message}
                aria-invalid={errors.message !== undefined}
                aria-describedby={describedBy('message')}
                onChange={(event) => {
                  update('message', event.target.value)
                }}
              />
              {errors.message === undefined ? null : (
                <p className="field__error" id={errorId('message')}>
                  {errors.message}
                </p>
              )}
            </div>

            <div className="field field--check">
              <input
                id={fieldId('consent')}
                name="consent"
                type="checkbox"
                checked={values.consent}
                aria-invalid={errors.consent !== undefined}
                aria-describedby={describedBy('consent')}
                onChange={(event) => {
                  update('consent', event.target.checked)
                }}
              />
              <label htmlFor={fieldId('consent')}>
                {form.consent}{' '}
                <a className="contact__privacy-link" href={pathFor('privacy')}>
                  {dictionary.footer.privacy}
                </a>
              </label>
              {errors.consent === undefined ? null : (
                <p className="field__error" id={errorId('consent')}>
                  {errors.consent}
                </p>
              )}
            </div>

            <input
              className="field__honeypot"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              onChange={(event) => {
                honeypot.current = event.target.value
              }}
            />

            <button
              className="button button--submit"
              type="submit"
              disabled={status === 'submitting'}
            >
              <span>{status === 'submitting' ? form.submitting : form.submit}</span>
              <span className="button__arrow" aria-hidden="true">
                →
              </span>
            </button>

            <div className="contact__status" role="status" aria-live="polite">
              {status === 'success' ? (
                <p className="contact__status-ok">
                  <strong>{form.successTitle}</strong> {form.successBody}
                </p>
              ) : null}
              {status === 'error' ? (
                <p className="contact__status-error">
                  <strong>{form.errorTitle}</strong> {form.errorBody}{' '}
                  <a href={`mailto:${siteConfig.salesEmail}`}>{form.fallbackHint}</a>
                </p>
              ) : null}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  )
}
